<template>
  <div
    ref="contextMenuRef"
    class="contextMenu"
    :class="{ 'contextMenu--fullWidth': $props.hasFullWidthOnMobile }"
  >
    <div
      ref="triggerRef"
      class="trigger"
    >
      <slot name="trigger"></slot>
    </div>

    <Transition
      appear
      :name="$props.animationIn"
    >
      <template v-if="modelValue">
        <div
          v-click-outside="{ handler: handleClickOutside, excluded: excludedSelectors }"
          class="menu"
        >
          <Transition
            appear
            name="fade-in"
          >
            <slot name="content"></slot>
          </Transition>
        </div>
      </template>
    </Transition>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

import vClickOutside from '@shared/directives/ClickOutside'

// INIT
const modelValue = defineModel({ type: Boolean })
const excludedSelectors = ['.close-button', '.trigger-btn', '.link']

defineProps({
  animationIn: {
    type: String,
    default: 'slide-from-top',
  },

  hasFullWidthOnMobile: {
    type: Boolean,
    default: false,
  },
})

// DATA
const contextMenuRef = ref(null)
const triggerRef = ref(null)
const topMenuPosition = ref(0)

// METHODS
function handleClickOutside() {
  window.setTimeout(() => {
    modelValue.value = false
  }, 250)
}

watch(modelValue, val => {
  if (val) {
    topMenuPosition.value = `${triggerRef.value.getBoundingClientRect().height}px`
  }
})
</script>

<style name="animations" scoped>
@keyframes slide-from-top {
  0% {
    transform: scaleY(0.4);
    transform-origin: 100% 0;
  }

  100% {
    transform: scaleY(1);
    transform-origin: 100% 0;
  }
}

@keyframes slide-from-bottom {
  0% {
    transform: scaleY(0.4);
    transform-origin: 0 100%;
  }

  100% {
    transform: scaleY(1);
    transform-origin: 0 100%;
  }
}

@keyframes fade-out {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

.slide-from-top-enter-active {
  animation-name: slide-from-top;
  animation-duration: var(--duration-medium-1);
  animation-timing-function: var(--ease-in-out-cubic);
}

.slide-from-top-leave-active {
  animation-name: fade-out;
  animation-duration: var(--duration-short-1);
  animation-timing-function: var(--linear);
}

.slide-from-bottom-enter-active {
  animation-name: slide-from-bottom;
  animation-duration: var(--duration-medium-1);
  animation-timing-function: var(--ease-in-out-cubic);
}

.slide-from-bottom-leave-active {
  animation-name: fade-out;
  animation-duration: var(--duration-short-1);
  animation-timing-function: var(--linear);
}

.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity var(--duration-short-1) var(--linear);
  transition-delay: var(--duration-medium-1);
}

.fade-in-enter-from,
.fade-in-leave-to {
  opacity: 0;
}

.fade-out-enter-active,
.fade-out-leave-active {
  transition: opacity var(--duration-short-1) var(--linear);
}

.fade-out-enter-from,
.fade-out-leave-to {
  opacity: 0;
}
</style>

<style scoped>
.contextMenu {
  position: relative;

  :deep(.close-button) {
    float: right;
  }
}

.menu {
  position: absolute;
  top: calc(v-bind(topMenuPosition) + var(--fixed-spacing-fix-01));
  right: 0;
  border-radius: var(--fixed-spacing-fix-02);
  z-index: var(--pet-stack-level-popup-menu);
  background-color: var(--surface);
  overflow: hidden;
  box-shadow: var(--elevation-level-3);
}

.contextMenu--fullWidth .menu {
  border-radius: var(--border-radius-res-l) var(--border-radius-res-l) var(--fixed-border-radius-fix-00)
    var(--fixed-border-radius-fix-00);

  position: fixed;
  top: initial;
  left: 0;
  bottom: 0;
  height: 96dvh;
}

@media (--v-medium) {
  .contextMenu--fullWidth .menu {
    position: absolute;
    left: initial;
    bottom: initial;
    top: calc(v-bind(topMenuPosition) + var(--fixed-spacing-fix-01));
    height: initial;
    border-radius: var(--fixed-spacing-fix-02);
  }
}
</style>
